import { ColumnState } from 'ag-grid-community';
import { makeReduxDuck } from 'teedux';
import { TRootState } from '../../../../store';
import { IGridEntityAttribute, ILayersData } from '../../../types';
import { ISourceSetModel } from '../types';

interface IState {
    sourceSetModels: {
        [sourceSetId: string]: ISourceSetModel;
    };

    lastMapClickPosition: null | { x: number; y: number };
    nearEventsRadius: null | number;
    mapClustering: boolean;
    monitoredObjectType: string | string[] | null;
    layersAttributes: IGridEntityAttribute[];
    customLayers: ILayersData[];
}

export const initialState: IState = {
    sourceSetModels: {},
    lastMapClickPosition: null,
    nearEventsRadius: null,
    mapClustering: true,
    monitoredObjectType: ['tanks'],
    layersAttributes: [],
    customLayers: [],
};

const duck = makeReduxDuck('ui/discovery/general', initialState);

const storeSourceSetModelAction = duck.defineAction<{
    filterModel: object;
    sortModel: ColumnState[];
    sourceSetId: string;
    sourceSetElementIds: Set<string> | null;
    quickFilter: string;
}>(
    'STORE_SOURCE_SET_MODEL',
    (
        { sourceSetModels },
        {
            sourceSetId,
            sourceSetElementIds,
            filterModel,
            sortModel,
            quickFilter,
        }
    ) => ({
        sourceSetModels: {
            ...sourceSetModels,
            [sourceSetId]: {
                sourceSetElementIds,
                filterModel,
                sortModel,
                quickFilter,
            },
        },
    })
);

const storeMapClickPositionAction = duck.defineAction<{
    x: number;
    y: number;
}>('STORE_MAP_CLICK_POSITION', (_, { x, y }) => ({
    lastMapClickPosition: { x, y },
}));

const storeNearEventsRadiusAction = duck.defineAction<{
    radius: number | null;
}>('STORE_NEAR_EVENTS_RADIUS', (_, { radius }) => ({
    nearEventsRadius: radius,
}));

const storeMapClustering = duck.defineAction<{
    state: boolean;
}>('STORE_MAP_CLUSTERING', (_, { state }) => ({
    mapClustering: state,
}));

const storeMonitoredObjectType = duck.defineAction<{
    type: string | string[] | null;
}>('STORE_MONITORED_OBJECT_TYPE', (_, { type }) => ({
    monitoredObjectType: type,
}));

const resetMapClickPositionAction = duck.definePayloadlessAction(
    'RESET_MAP_CLICK_POSITION',
    () => ({
        lastMapClickPosition: null,
    })
);

const storeLayersAction = duck.defineAction<{
    data: {
        attributes: IGridEntityAttribute[];
        layersData: ILayersData[];
    };
}>('STORE_LAYERS_ATTRIBUTES', (_, { data }) => ({
    layersAttributes: data.attributes,
    customLayers: data.layersData,
}));

export const storeSourceSetModel = (
    sourceSetId: string,
    data: ISourceSetModel
) =>
    storeSourceSetModelAction({
        sourceSetId,
        ...data,
    });

export const storeLastMapClickPosition = (x: number, y: number) =>
    storeMapClickPositionAction({
        x,
        y,
    });

export const storeNearEventsRadius = (radius: number | null) =>
    storeNearEventsRadiusAction({ radius });

export const setMapClustering = (state: boolean) =>
    storeMapClustering({
        state,
    });

export const setMonitoredObjectType = (type: string | string[] | null) =>
    storeMonitoredObjectType({
        type,
    });

export const resetLastMapClickPosition = () => resetMapClickPositionAction();

export const storeLayers = (data: {
    attributes: IGridEntityAttribute[];
    layersData: ILayersData[];
}) => storeLayersAction({ data });

export const getLayersAttributes = (state: TRootState) =>
    state.ui.discovery.general.layersAttributes;

export const getCustomLayers = (state: TRootState) =>
    state.ui.discovery.general.customLayers;

export const getMonitoredObjectType = (state: TRootState) =>
    state.ui.discovery.general.monitoredObjectType;

export default duck.getReducer();
